import {useState, useCallback} from "react";
import {
	GridFilterModel,
	GridSortModel,
	GridInitialState,
	GridCallbackDetails
} from "@mui/x-data-grid-pro";
import {useListCourseLessons, type CourseLessonDto} from "@/app2/shared/data";
import type {
	CoursesControllerListLessonsParams,
	CourseLessonAttributeFilterDto,
	CourseLessonAttributeFilterDtoField,
	CourseLessonAttributeFilterDtoOperator,
	CourseLessonFiltersDtoOperator
} from "@/app2/shared/data/gen";

const SORT_FIELDS: readonly string[] = ["title", "updatedAt", "videoDuration"];

const FILTERABLE_FIELDS: readonly CourseLessonAttributeFilterDtoField[] = [
	"title",
	"videoDuration",
	"updatedAt",
	"languages"
];

interface UseCourseLessonsListGridProps {
	courseId: number | undefined;
}

const defaultFilterSettings: CoursesControllerListLessonsParams = {
	page: 1,
	pageSize: 25,
	orderBy: "courseCurriculumItemOrder",
	orderDirection: "asc"
};

export function useCourseLessonsListGrid({
	courseId
}: UseCourseLessonsListGridProps): {
		rows: CourseLessonDto[];
		rowCount: number;
		isLoading: boolean;
		isFetched: boolean;
		refetch: () => void;
		page: number;
		pageSize: number;
		onFilterChange: (model: GridFilterModel, details: GridCallbackDetails<"filter">) => void;
		onPageChange: (page: number) => void;
		onPageSizeChange: (pageSize: number) => void;
		onSortModelChange: (model: GridSortModel) => void;
		defaultInitialState: GridInitialState;
	} {
	const [filterModel, setFilterModel] = useState<CoursesControllerListLessonsParams>(defaultFilterSettings);

	const {data, isLoading, isFetched, refetch} = useListCourseLessons(courseId, filterModel);

	const rows = data?.data ?? [];
	const rowCount = data?.total ?? 0;

	const onPageChange = useCallback((page: number) => {
		setFilterModel((prev) => ({
			...prev,
			page: page + 1
		}));
	}, []);

	const onPageSizeChange = useCallback((pageSize: number) => {
		setFilterModel((prev) => ({
			...prev,
			pageSize,
			page: 1
		}));
	}, []);

	const onFilterChange = useCallback(
		(newFilterModel: GridFilterModel, details: GridCallbackDetails<"filter">) => {
			setFilterModel((prev) => {
				const conditions: CourseLessonAttributeFilterDto[] = (newFilterModel.items ?? [])
					.filter(
						(item: {
							columnField?: string;
							field?: string;
							value?: unknown;
							operatorValue?: string;
							operator?: string;
						}) => {
							const field = item.columnField ?? item.field;
							const hasValue =
								item.value !== undefined &&
								item.value !== null &&
								String(item.value).trim() !== "";
							const operator = item.operatorValue ?? item.operator ?? "co";
							return (
								field &&
								(FILTERABLE_FIELDS as readonly string[]).includes(field) &&
								(operator === "pr" || hasValue)
							);
						}
					)
					.map(
						(item: {
							columnField?: string;
							field?: string;
							value?: unknown;
							operatorValue?: string;
							operator?: string;
						}): CourseLessonAttributeFilterDto => {
							const field = (item.columnField ?? item.field) as CourseLessonAttributeFilterDtoField;
							const operator = (item.operatorValue ??
								item.operator ??
								"co") as CourseLessonAttributeFilterDtoOperator;
							let value: string | undefined;
							if (operator === "pr") {
								value = undefined;
							} else if (item.value instanceof Date) {
								value = item.value.toISOString();
							} else {
								value = String(item.value ?? "");
							}
							return {field, value, operator};
						}
					);

				const filtersDto: CoursesControllerListLessonsParams["filters"] =
					conditions.length > 0
						? {
							operator: (newFilterModel.linkOperator ?? "and") as CourseLessonFiltersDtoOperator,
							conditions
						}
						: undefined;

				return {
					...prev,
					page: 1,
					filters: filtersDto
				};
			});
		},
		[]
	);

	const onSortModelChange = useCallback((sortModel: GridSortModel) => {
		setFilterModel((prev) => ({
			...prev,
			orderBy:
				sortModel.length > 0 && SORT_FIELDS.includes(sortModel[0].field)
					? (sortModel[0].field as "title" | "updatedAt" | "videoDuration")
					: "updatedAt",
			orderDirection: sortModel.length > 0 ? (sortModel[0].sort as "asc" | "desc") : "desc"
		}));
	}, []);

	const defaultInitialState: GridInitialState = {
		pagination: {
			pageSize: filterModel.pageSize ?? 25,
			page: (filterModel.page ?? 1) - 1
		},
		sorting: {
			sortModel: [{field: "updatedAt", sort: "desc"}]
		}
	};

	return {
		rows,
		rowCount,
		isLoading,
		isFetched,
		refetch,
		page: (filterModel.page ?? 1) - 1,
		pageSize: filterModel.pageSize ?? 25,
		onFilterChange,
		onPageChange,
		onPageSizeChange,
		onSortModelChange,
		defaultInitialState
	};
}
